import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import SearchIcon from "@mui/icons-material/Search";
import { toast } from "react-toastify";
import Api from "../Api";
import { BASE_API_URL } from "../const";
import blockStore from "../../Store/BlockStore";
import channelStore from "../../Store/ChannelStore";
import { getErrorMessage } from "../../utils/errorHandler";

const getMovieTitle = (movie) =>
  movie?.title || movie?.name_rus || movie?.name || `Фильм ${movie?.id}`;

const getFillerTitle = (filler) =>
  filler?.file?.title || filler?.file?.name || "—";

const getFileId = (file) => Number(file?.file_id || file?.id || 0);

const toArray = (value) => {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.data)) return value.data;
  return [];
};

const getApiErrorMessage = (error, fallback) => {
  const errors = error?.response?.data?.errors;
  if (errors && typeof errors === "object") {
    const first = Object.values(errors).flat().find(Boolean);
    if (first) return String(first);
  }
  return getErrorMessage(error, fallback);
};

const MainBlockFillers = ({ open }) => {
  const [query, setQuery] = useState("");
  const [movies, setMovies] = useState([]);
  const [searching, setSearching] = useState(false);
  const [hoveredPoster, setHoveredPoster] = useState(null);
  const [posterPosition, setPosterPosition] = useState({ top: 0, left: 0 });

  const [fillers, setFillers] = useState([]);
  const [loadingFillers, setLoadingFillers] = useState(false);
  const [removingId, setRemovingId] = useState(null);

  const [assignmentOpen, setAssignmentOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [selectedMovie, setSelectedMovie] = useState(null);
  const [movieFiles, setMovieFiles] = useState([]);
  const [loadingMovie, setLoadingMovie] = useState(false);
  const [selectedFileId, setSelectedFileId] = useState("");
  const [startsOn, setStartsOn] = useState("");
  const [endsOn, setEndsOn] = useState("");
  const [saving, setSaving] = useState(false);

  const searchRequestRef = useRef(0);

  const loadFillers = useCallback(async () => {
    setLoadingFillers(true);
    try {
      const result = await blockStore.getBlockFillers();
      setFillers(toArray(result));
    } catch (error) {
      setFillers([]);
      toast.error(getApiErrorMessage(error, "Не удалось загрузить перебивки"));
    } finally {
      setLoadingFillers(false);
    }
  }, []);

  useEffect(() => {
    if (open) loadFillers();
  }, [open, loadFillers]);

  useEffect(() => {
    if (!open) {
      setAssignmentOpen(false);
      setHoveredPoster(null);
    }
  }, [open]);

  const searchMovies = useCallback(async (searchQuery) => {
    const trimmedQuery = searchQuery.trim();
    if (!trimmedQuery) {
      setMovies([]);
      setSearching(false);
      return;
    }

    const requestId = ++searchRequestRef.current;
    setSearching(true);
    try {
      const params = new URLSearchParams({ page: "1", query: trimmedQuery });
      const response = await Api.post(`${BASE_API_URL}/api/filter?${params.toString()}`, {});
      if (requestId !== searchRequestRef.current) return;

      const payload = response.data?.data;
      const rows = Array.isArray(payload?.data)
        ? payload.data
        : Array.isArray(payload)
          ? payload
          : [];
      setMovies(rows);
    } catch (error) {
      if (requestId === searchRequestRef.current) {
        setMovies([]);
        toast.error(getApiErrorMessage(error, "Не удалось выполнить поиск фильмов"));
      }
    } finally {
      if (requestId === searchRequestRef.current) setSearching(false);
    }
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    const timer = setTimeout(() => searchMovies(query), 350);
    return () => clearTimeout(timer);
  }, [query, open, searchMovies]);

  const handlePosterEnter = (event, movie) => {
    if (!movie.poster) return;
    const rect = event.currentTarget.getBoundingClientRect();
    const width = 200;
    const height = 300;
    setPosterPosition({
      top: Math.max(10, Math.min(rect.top, window.innerHeight - height - 10)),
      left: Math.max(10, Math.min(rect.right + 12, window.innerWidth - width - 10)),
    });
    setHoveredPoster(movie.poster);
  };

  const openFilmPage = (movieId) => {
    const params = new URLSearchParams({ id: movieId });
    window.open(`/filmdata?${params.toString()}`, "_blank", "noopener,noreferrer");
  };

  const handleMovieMouseDown = (event, movie) => {
    if (event.button === 1) {
      event.preventDefault();
      openFilmPage(movie.id);
    }
  };

  const handleMovieClick = async (movie) => {
    setHoveredPoster(null);
    setAssignmentOpen(true);
    setEditingId(null);
    setSelectedMovie(movie);
    setMovieFiles([]);
    setSelectedFileId("");
    setStartsOn("");
    setEndsOn("");
    setLoadingMovie(true);

    try {
      const response = await Api.get(`${BASE_API_URL}/api/movie/${movie.id}`);
      const details = response.data?.data || response.data;
      const files = Array.isArray(details?.files) ? details.files : [];
      setSelectedMovie(details || movie);
      setMovieFiles(files);

      const playableFile = files.find(
        (file) => getFileId(file) && Number(file.duration) > 0
      );
      if (playableFile) setSelectedFileId(getFileId(playableFile));
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Не удалось загрузить файлы фильма"));
    } finally {
      setLoadingMovie(false);
    }
  };

  const handleEdit = (filler) => {
    setEditingId(filler.id);
    setSelectedMovie({ title: getFillerTitle(filler) });
    setMovieFiles([]);
    setSelectedFileId(filler.file_id || "");
    setStartsOn(String(filler.starts_on || "").slice(0, 10));
    setEndsOn(String(filler.ends_on || "").slice(0, 10));
    setLoadingMovie(false);
    setAssignmentOpen(true);
  };

  const resetAssignment = () => {
    setAssignmentOpen(false);
    setEditingId(null);
    setSelectedMovie(null);
    setMovieFiles([]);
    setSelectedFileId("");
    setStartsOn("");
    setEndsOn("");
  };

  const closeAssignment = () => {
    if (saving) return;
    resetAssignment();
  };

  const handleSave = async () => {
    const wasEditing = Boolean(editingId);
    const fileId = Number(selectedFileId);
    if (!Number.isInteger(fileId) || fileId <= 0) {
      toast.error("Выберите корректный файл");
      return;
    }
    if (!editingId) {
      const selectedFile = movieFiles.find((file) => getFileId(file) === fileId);
      if (!selectedFile || Number(selectedFile.duration) <= 0) {
        toast.error("Выберите файл с положительной длительностью");
        return;
      }
    }
    if (!startsOn || !endsOn) {
      toast.error("Выберите обе даты периода");
      return;
    }
    if (endsOn < startsOn) {
      toast.error("Дата «По» не может быть раньше даты «С»");
      return;
    }
    setSaving(true);
    try {
      const payload = { file_id: fileId, starts_on: startsOn, ends_on: endsOn };
      if (editingId) {
        await blockStore.updateBlockFiller({ id: editingId, ...payload });
      } else {
        await blockStore.assignBlockFiller(payload);
      }
      await Promise.all([loadFillers(), channelStore.getChannels()]);
      resetAssignment();
      toast.success(
        wasEditing
          ? "Перебивка обновлена. Выполните restart блочных каналов"
          : "Перебивка создана. Выполните restart блочных каналов"
      );
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Не удалось сохранить перебивку"));
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async (filler) => {
    if (!window.confirm(`Удалить перебивку ID ${filler.id}?`)) return;

    setRemovingId(filler.id);
    try {
      await blockStore.removeBlockFiller(filler.id);
      await Promise.all([loadFillers(), channelStore.getChannels()]);
      toast.success("Перебивка удалена. Выполните restart блочных каналов");
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Не удалось удалить перебивку"));
    } finally {
      setRemovingId(null);
    }
  };

  return (
    <Box sx={{ position: "relative" }}>
      <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
        <TextField
          fullWidth
          size="small"
          label="Фильтр по названию"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter") searchMovies(query);
          }}
        />
        <Button
          variant="contained"
          startIcon={<SearchIcon />}
          onClick={() => searchMovies(query)}
          disabled={searching || !query.trim()}
        >
          Найти
        </Button>
      </Box>

      <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
        Результаты поиска
      </Typography>
      <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 250, mb: 3 }}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell width={100}>ID</TableCell>
              <TableCell>Название</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {searching ? (
              <TableRow>
                <TableCell colSpan={2} align="center"><CircularProgress size={26} /></TableCell>
              </TableRow>
            ) : movies.length ? (
              movies.map((movie) => (
                <TableRow
                  key={movie.id}
                  hover
                  onClick={() => handleMovieClick(movie)}
                  onMouseDown={(event) => handleMovieMouseDown(event, movie)}
                  onMouseEnter={(event) => handlePosterEnter(event, movie)}
                  onMouseLeave={() => setHoveredPoster(null)}
                  sx={{ cursor: "pointer" }}
                  title="ЛКМ — назначить перебивку, средняя кнопка — открыть фильм"
                >
                  <TableCell>{movie.id}</TableCell>
                  <TableCell>{getMovieTitle(movie)}</TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={2} align="center" sx={{ color: "text.secondary" }}>
                  {query.trim() ? "Ничего не найдено" : "Введите название фильма"}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
        Назначенные перебивки
      </Typography>
      <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 360 }}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>File ID</TableCell>
              <TableCell>Название</TableCell>
              <TableCell>Формат</TableCell>
              <TableCell>С</TableCell>
              <TableCell>По</TableCell>
              <TableCell>Действия</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loadingFillers ? (
              <TableRow>
                <TableCell colSpan={7} align="center"><CircularProgress size={26} /></TableCell>
              </TableRow>
            ) : fillers.length ? (
              fillers.map((filler) => (
                <TableRow key={filler.id}>
                  <TableCell>{filler.id}</TableCell>
                  <TableCell>{filler.file_id}</TableCell>
                  <TableCell>{getFillerTitle(filler)}</TableCell>
                  <TableCell>{filler.format ?? "—"}</TableCell>
                  <TableCell>{String(filler.starts_on || "").slice(0, 10)}</TableCell>
                  <TableCell>{String(filler.ends_on || "").slice(0, 10)}</TableCell>
                  <TableCell sx={{ whiteSpace: "nowrap" }}>
                    <IconButton
                      color="primary"
                      size="small"
                      onClick={() => handleEdit(filler)}
                      title="Редактировать перебивку"
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      color="error"
                      size="small"
                      disabled={removingId === filler.id}
                      onClick={() => handleRemove(filler)}
                      title="Удалить перебивку"
                    >
                      {removingId === filler.id ? <CircularProgress size={18} /> : <DeleteIcon />}
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={7} align="center">Перебивки не назначены</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {hoveredPoster && (
        <Box
          component="img"
          src={hoveredPoster}
          alt="Заставка фильма"
          sx={{
            position: "fixed",
            top: posterPosition.top,
            left: posterPosition.left,
            width: 200,
            height: 300,
            objectFit: "cover",
            borderRadius: 1,
            boxShadow: 8,
            zIndex: 1600,
            pointerEvents: "none",
          }}
        />
      )}

      <Dialog open={assignmentOpen} onClose={closeAssignment} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingId ? "Редактировать" : "Назначить"} перебивку: {getMovieTitle(selectedMovie)}
        </DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 2 }}>
          {loadingMovie ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              {editingId ? (
                <TextField
                  fullWidth
                  type="number"
                  label="ID файла"
                  value={selectedFileId}
                  onChange={(event) => setSelectedFileId(event.target.value)}
                  inputProps={{ min: 1, step: 1 }}
                />
              ) : (
                <FormControl fullWidth>
                  <InputLabel>Выберите качество</InputLabel>
                  <Select
                    label="Выберите качество"
                    value={selectedFileId}
                    onChange={(event) => setSelectedFileId(event.target.value)}
                  >
                    {movieFiles.map((file) => (
                      <MenuItem
                        key={getFileId(file)}
                        value={getFileId(file)}
                        disabled={Number(file.duration) <= 0}
                      >
                        ID {getFileId(file)} — {file.name || file.path || file.link || "Файл"}
                        {file.format ? ` — ${file.format}` : ""}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}

              <Box sx={{ display: "flex", gap: 2 }}>
                <TextField
                  fullWidth
                  required
                  type="date"
                  label="С"
                  value={startsOn}
                  onChange={(event) => setStartsOn(event.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  fullWidth
                  required
                  type="date"
                  label="По"
                  value={endsOn}
                  onChange={(event) => setEndsOn(event.target.value)}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ min: startsOn || undefined }}
                  helperText="Обе даты входят в период"
                />
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeAssignment} disabled={saving}>Отмена</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving || loadingMovie || (!editingId && !movieFiles.length)}
          >
            {saving ? <CircularProgress size={22} color="inherit" /> : "Сохранить"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MainBlockFillers;
